import React, { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import IconButton from 'components/IconButton';
import Modal from 'components/Modal/Modal';
import RoundedButton from 'components/RoundedButton/RoundedButton';
import SpinnerRing from 'components/SpinnerRing';

import { REASON_MAX_LENGTH, REASON_MIN_LENGTH } from '../constants';
import styles from '../DbSnapshots.module.scss';
import { useQueryTenantSearch } from '../hooks/useDbSnapshots';

const validateReason = (value) => {
  const trimmed = (value || '').trim();
  if (trimmed.length === 0) return 'Reason is required';
  if (trimmed.length < REASON_MIN_LENGTH) return `Reason must be at least ${REASON_MIN_LENGTH} characters`;
  if (trimmed.length > REASON_MAX_LENGTH) return `Reason cannot exceed ${REASON_MAX_LENGTH} characters`;
  return '';
};

const MIN_SEARCH_LENGTH = 2;

const extractApiError = (error) => {
  if (!error) return '';
  const apiMessage = error?.response?.data?.message;
  if (Array.isArray(apiMessage)) return apiMessage.join(' ');
  if (typeof apiMessage === 'string') return apiMessage;
  return error?.message || 'Request failed';
};

const RequestSnapshotModal = ({ isOpen, onClose, onSubmit, isSubmitting, submitError }) => {
  const [tenantQuery, setTenantQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [tenantTouched, setTenantTouched] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);

  const searchEnabled = isOpen && !selectedTenant && tenantQuery.trim().length >= MIN_SEARCH_LENGTH;

  const { isLoading: isLoadingTenants, data: tenantsData } = useQueryTenantSearch(tenantQuery.trim(), {
    enabled: searchEnabled,
  });

  const tenants = useMemo(
    () =>
      (tenantsData?.data || []).map((account) => ({
        subdomain: account.subdomain,
        customer: account.customer,
      })),
    [tenantsData]
  );

  useEffect(() => {
    if (!isOpen) {
      setTenantQuery('');
      setSelectedTenant(null);
      setReason('');
      setConfirming(false);
      setTenantTouched(false);
      setReasonTouched(false);
    }
  }, [isOpen]);

  const showDropdown = !selectedTenant && searchEnabled && tenants.length > 0;
  const tenantError = tenantTouched && !selectedTenant ? 'Tenant is required' : '';
  const reasonError = reasonTouched ? validateReason(reason) : '';
  const isFormValid = !!selectedTenant && !validateReason(reason);

  const handleConfirm = () => {
    setTenantTouched(true);
    setReasonTouched(true);
    if (!isFormValid) return;
    setConfirming(true);
  };

  const handleSubmit = () => {
    onSubmit({
      tenantId: selectedTenant.subdomain,
      reason: reason.trim(),
    });
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} showHeader={false} shouldCloseOnOverlayClick={false}>
      {isSubmitting ? (
        <div
          className='d-flex justify-content-center align-items-center py-5'
          style={{ minHeight: 240 }}
          data-testid='snapshot-submit-spinner'
        >
          <SpinnerRing width='80px' height='80px' />
        </div>
      ) : (
        <div className='py-4 ps-5 pe-5'>
          <div className='border-btm d-flex justify-content-between pt-0 px-0 pb-3'>
            <h5 className='modal-title fw-bold-bold'>Request DB Snapshot</h5>
            <IconButton icon='icon-cross-x' onClick={onClose} />
          </div>
          <div className='pt-4'>
            {!confirming ? (
              <>
                <div className='row'>
                  <div className='form-group input-hight-acm col-12 mb-3'>
                    <label htmlFor='dbsnapshot-tenant' className='label-title'>
                      Tenant <span className='text-danger'>*</span>
                    </label>
                    <input
                      id='dbsnapshot-tenant'
                      type='text'
                      autoComplete='off'
                      placeholder='Search tenant by subdomain…'
                      value={selectedTenant ? selectedTenant.subdomain : tenantQuery}
                      onChange={(event) => {
                        setSelectedTenant(null);
                        setTenantQuery(event.target.value);
                      }}
                      onBlur={() => setTenantTouched(true)}
                      className={cx('form-control newClientInputs', { 'is-invalid': tenantError })}
                    />
                    {tenantError && <div className='invalid-feedback'>{tenantError}</div>}
                    {isLoadingTenants && (
                      <p className='text-muted mt-1' style={{ fontSize: 12 }}>
                        Loading…
                      </p>
                    )}
                    {showDropdown && (
                      <ul className={styles.typeahead} data-testid='tenant-typeahead'>
                        {tenants.map((tenant) => (
                          <li key={tenant.subdomain}>
                            <button
                              type='button'
                              className={styles.typeaheadItem}
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setTenantQuery('');
                              }}
                            >
                              <span className='fw-bold'>{tenant.subdomain}</span>
                              {tenant.customer && <span className='text-muted ms-2'>{tenant.customer}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className='form-group input-hight-acm col-12 mb-3'>
                    <label htmlFor='dbsnapshot-reason' className='label-title'>
                      Reason <span className='text-danger'>*</span>
                    </label>
                    <textarea
                      id='dbsnapshot-reason'
                      name='reason'
                      rows={4}
                      maxLength={REASON_MAX_LENGTH}
                      placeholder={`Explain why this snapshot is needed (min ${REASON_MIN_LENGTH} chars)`}
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value);
                        setReasonTouched(true);
                      }}
                      onBlur={() => setReasonTouched(true)}
                      className={cx('form-control newClientInputs', { 'is-invalid': reasonError })}
                    />
                    <div className='d-flex justify-content-between mt-1'>
                      <div>{reasonError && <span className='invalid-feedback d-block'>{reasonError}</span>}</div>
                      <small className='text-muted'>
                        {reason.length}/{REASON_MAX_LENGTH}
                      </small>
                    </div>
                  </div>
                </div>
                <div className='d-flex align-items-center justify-content-end gap-2 p-3 border-t'>
                  <RoundedButton text='Cancel' btnStyle='outlined' size='md' onClick={onClose} />
                  <RoundedButton
                    text='Continue'
                    btnStyle='contained'
                    color='primary'
                    size='md'
                    onClick={handleConfirm}
                    isDisabled={!isFormValid}
                  />
                </div>
              </>
            ) : (
              <>
                <div className={cx(styles.confirmBox, 'mb-3')}>
                  <h5 className='mb-2'>Confirm snapshot request</h5>
                  <p>
                    Tenant: <span className='fw-bold'>{selectedTenant.subdomain}</span>
                    {selectedTenant.customer && <span className='text-muted ms-2'>({selectedTenant.customer})</span>}
                  </p>
                  <p className='text-muted' style={{ fontSize: 12 }}>
                    PII will be masked. Snapshot package is one-time download and expires automatically.
                  </p>
                  {submitError && (
                    <p className='text-danger mb-0' role='alert' data-testid='submit-error'>
                      {extractApiError(submitError)}
                      {submitError?.response?.status === 409 && selectedTenant?.subdomain && (
                        <span className='text-muted d-block mt-1' style={{ fontSize: 12 }}>
                          Download or revoke the existing snapshot for{' '}
                          <span className='fw-bold'>{selectedTenant.subdomain}</span> from the list before requesting a
                          new one.
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div className='d-flex align-items-center justify-content-end gap-2 p-3 border-t'>
                  <RoundedButton text='Back' btnStyle='outlined' size='md' onClick={() => setConfirming(false)} />
                  <RoundedButton
                    text='Request snapshot'
                    btnStyle='contained'
                    color='primary'
                    size='md'
                    onClick={handleSubmit}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

RequestSnapshotModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  submitError: PropTypes.object,
};

RequestSnapshotModal.defaultProps = {
  isSubmitting: false,
  submitError: null,
};

export { validateReason };
export default RequestSnapshotModal;
